import { useMutation } from '@apollo/client'
import { useTheme } from '@emotion/react'
import { Delete as DeleteIcon, PushPin } from '@mui/icons-material'
import { Box, Button, TextField, Typography } from '@mui/material'
import { Underline } from '@tiptap/extension-underline'
import { StarterKit } from '@tiptap/starter-kit'
import {
    MenuButtonBold,
    MenuButtonBulletedList,
    MenuButtonItalic,
    MenuButtonOrderedList,
    MenuButtonUnderline,
    MenuControlsContainer,
    MenuDivider,
    MenuSelectHeading,
    RichTextEditor,
    type RichTextEditorRef,
} from 'mui-tiptap'
import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
    AddCompanyNote,
    DeleteCompanyNote,
    UpdateCompanyNote,
} from 'recruiter/graphql/companyNotes.gql'
import useToast from 'shared/components/Toast/useToast'
import { getPxAsRem } from 'shared/utils'
import { stripHtmlTags } from 'shared/utils/stripTags'
import { StyledRichTextEditor } from './styled'

import type { GetCompanyQuery } from '__generated__/graphql'
import type { FC } from 'react'
import DrawerComponent from 'shared/components/DrawerComponent'

interface EditCompanyProps {
    open: boolean
    onClose: () => void
    note: NonNullable<GetCompanyQuery['company']['notes']>[number] | null
    refetch: () => void
}

const EditNotes: FC<EditCompanyProps> = ({ open, onClose, note, refetch }) => {
    const rteRef = useRef<RichTextEditorRef>(null)
    const toast = useToast()
    const theme = useTheme()
    const [addCompanyNote] = useMutation(AddCompanyNote)
    const [editCompanyNote] = useMutation(UpdateCompanyNote)
    const [deleteCompanyNote] = useMutation(DeleteCompanyNote)
    const [noteTitle, setNoteTitle] = useState<string>('')

    const companyId = useParams().companyId ?? ''
    const noteContent = rteRef.current?.editor?.getHTML()
    const setNoteContent = (noteContent) =>
        rteRef.current?.editor?.commands.setContent(noteContent)

    useEffect(() => {
        if (note) {
            setNoteTitle(note.title || '')
            setNoteContent(note.noteData || '')
        } else {
            setNoteTitle('')
            setNoteContent('')
        }
    }, [note])

    const handleClose = () => {
        onClose()
        setNoteTitle('')
        setNoteContent('')
    }

    const handleSaveNote = async () => {
        const content = rteRef.current?.editor?.getHTML() || ''
        if (!noteTitle || !stripHtmlTags(content)) {
            toast({
                title: 'Error!',
                message: 'Please add a title and enter the note content.',
                severity: 'error',
            })
            return
        }

        const onCompleted = () => {
            refetch()
            handleClose()
            toast({
                title: 'Success!',
                message: note
                    ? 'Note updated successfully.'
                    : 'Note added successfully.',
                severity: 'success',
            })
        }

        const onError = (error) => {
            console.error(error)
            toast({
                title: 'Error!',
                message: 'Something went wrong. Please try again later.',
                severity: 'error',
            })
        }

        if (note) {
            await editCompanyNote({
                variables: {
                    noteId: note.id,
                    title: noteTitle,
                    note: content,
                },
                onCompleted,
                onError,
            })
        } else {
            await addCompanyNote({
                variables: {
                    companyId,
                    title: noteTitle,
                    note: content,
                },
                onCompleted,
                onError,
            })
        }
    }

    const handleCancel = () => {
        onClose()
        setNoteTitle('')
        setNoteContent('')
    }

    const handleDeleteNote = async () => {
        if (!note) return
        await deleteCompanyNote({
            variables: { noteId: note.id },
            onCompleted: () => {
                refetch()
                onClose()
                toast({
                    title: 'Success!',
                    message: 'Note deleted successfully.',
                    severity: 'success',
                })
            },
            onError: (error) => {
                console.error(error)
                toast({
                    title: 'Error!',
                    message: 'Something went wrong. Please try again later.',
                    severity: 'error',
                })
            },
        })
    }

    return (
        <DrawerComponent
            headerTitle={note ? 'Edit Note' : 'Add Note'}
            open={open}
            hasCancelButton
            onCancel={handleCancel}
            onClose={onClose}
            primaryActionText={note ? 'Update' : 'Add'}
            saveButtonDisabled={
                !noteTitle || !rteRef.current?.editor?.getHTML()
            }
            onPrimaryAction={handleSaveNote}
        >
            <Box sx={{ marginTop: 5 }}>
                <TextField
                    label="Note Title"
                    variant="outlined"
                    sx={{ marginBottom: getPxAsRem(16) }}
                    fullWidth
                    value={noteTitle}
                    onChange={(e) => setNoteTitle(e.target.value)}
                />
                {note?.isPinned && (
                    <Box
                        display="flex"
                        alignItems="center"
                        sx={{ marginBottom: getPxAsRem(16) }}
                    >
                        <PushPin
                            sx={{ color: theme.palette.other.light_blue }}
                        />
                        <Typography
                            variant="h6"
                            sx={{ marginLeft: getPxAsRem(8) }}
                        >
                            Pinned Note
                        </Typography>
                    </Box>
                )}
                <div>
                    <StyledRichTextEditor>
                        <RichTextEditor
                            ref={rteRef}
                            extensions={[StarterKit, Underline]}
                            content={note ? noteContent : ''}
                            renderControls={() => (
                                <MenuControlsContainer>
                                    <MenuSelectHeading />
                                    <MenuDivider />
                                    <MenuButtonBold />
                                    <MenuButtonItalic />
                                    <MenuButtonUnderline />
                                    <MenuButtonBulletedList />
                                    <MenuButtonOrderedList />
                                </MenuControlsContainer>
                            )}
                        />
                    </StyledRichTextEditor>
                </div>
                <Box
                    display="flex"
                    justifyContent="flex-start"
                    alignItems="center"
                    sx={{ marginBottom: getPxAsRem(16) }}
                >
                    {note && (
                        <Button
                            onClick={handleDeleteNote}
                            startIcon={<DeleteIcon />}
                            variant="text"
                            color="error"
                        >
                            Delete Note
                        </Button>
                    )}
                </Box>
            </Box>
        </DrawerComponent>
    )
}

export default EditNotes
